import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { CopilotSettingsStatus } from './useCustomerCopilotSettings'

interface CustomerCopilotSettingsModalProps {
  status: CopilotSettingsStatus
  errorKey?: string
  showNoKeyInstalled: boolean
  copilotEnabled: boolean
  copilotApiToken: string
  onCopilotEnabledChange: (copilotEnabled: boolean) => void
  onCopilotApiTokenChange: (copilotApiToken: string) => void
  onSave: () => void
  onClose: () => void
}

function CustomerCopilotSettingsModal(props: Readonly<CustomerCopilotSettingsModalProps>) {
  const {
    status,
    errorKey,
    showNoKeyInstalled,
    copilotEnabled,
    copilotApiToken,
    onCopilotEnabledChange,
    onCopilotApiTokenChange,
    onSave,
    onClose,
  } = props

  const { t } = useTranslation()

  const isLoading = status === 'loading'
  const isSaving = status === 'saving'
  const isEditable = !isLoading && !isSaving

  return (
    <Modal
      show
      onHide={isSaving ? undefined : onClose}
      backdrop={isSaving ? 'static' : true}
      keyboard={!isSaving}
      dialogClassName="modal"
      size="lg"
      aria-labelledby="copilot-settings-title"
      animation={false}
      centered
    >
      <Modal.Header>
        <Modal.Title className="d-flex align-self-center">
          <h2 id="copilot-settings-title">{t('customers.copilotSettings')}</h2>
        </Modal.Title>
        <button
          type="button"
          className="btn btn-link no-underline btn-lg d-flex justify-content-center align-items-center px-0"
          onClick={onClose}
          disabled={isSaving}
          data-testid="copilot-settings-close"
        >
          <span>{t('buttons.close')}</span>
          <i className="fal fa-times fa-2x ms-2" />
        </button>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="d-flex justify-content-center" data-testid="copilot-settings-loading">
            <LoadingSpinner />
            <span className="ms-2">{t('common.loading')}</span>
          </div>
        )}

        {errorKey !== undefined && (
          <div className="alert alert-warning" role="alert" data-testid="copilot-settings-error">
            {t(errorKey)}
          </div>
        )}

        {!isLoading && (
          <>
            <div className="form-check form-switch mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="copilot-settings-enabled"
                checked={copilotEnabled}
                disabled={!isEditable}
                onChange={(event) => onCopilotEnabledChange(event.target.checked)}
              />
              <label className="form-check-label ps-1" htmlFor="copilot-settings-enabled">
                {t('customers.copilotAccess')}
              </label>
            </div>

            <label className="form-label" htmlFor="copilot-settings-value">
              {t('customers.copilotApiToken')}
            </label>
            <input
              className="form-control"
              type="text"
              id="copilot-settings-value"
              name="copilot-settings-value"
              autoComplete="off"
              spellCheck={false}
              data-1p-ignore
              data-lpignore="true"
              value={copilotApiToken}
              disabled={!isEditable}
              onChange={(event) => onCopilotApiTokenChange(event.target.value)}
            />

            {showNoKeyInstalled && (
              <p className="mt-2 mb-0" data-testid="copilot-settings-no-key">
                {t('customers.copilotNoKeyInstalled')}
              </p>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          variant="primary"
          onClick={onSave}
          disabled={!isEditable}
          data-testid="copilot-settings-save"
        >
          {isSaving && <LoadingSpinner />}
          {!isSaving && <span>{t('buttons.save')}</span>}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CustomerCopilotSettingsModal
